import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ProviderType } from 'src/types/provider';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import { EDIT_KEYS, EditKeysType, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import RenderComponents from './RenderComponents';
import {
    clearProviderDetail,
    createProviderDetailFetch,
    getProviderDetailFetch,
    updateProviderDetailFetch
} from 'src/resources/providerDetail/reducer';
import { getServiceCodeFromData, getServiceCodeObject } from './functions';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { SelectValueType } from 'src/components/Select';
import { getUsersListFetch } from 'src/resources/usersList/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { setStorageData } from 'src/resources/storage/reducer';
import isDeepEqual from 'src/utils/isDeepEqual';
import { samePaths } from 'src/utils/checkPaths';
import deleteEmptyKeysAndValues from 'src/utils/deleteEmptyKeysAndValues';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { getProvidersListFetch, getWaitingProvidersListFetch } from '../../resources/providersList/reducer';
import { getApplicationInstallFetch } from 'src/resources/applicationInstall/reducer';

const initData = { ACTIVE: 1, DEFAULT_ALLOW_EXPEDITION: 1, PROVIDER_USERS: [], SERVICE_CODES: [], FILES: [], STATUS: 'created'};
const ProviderDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idProvider } = useParams();

    const newProvider = !idProvider;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.providerDetail.data);
    const usersData = useSelector((state: RootStoreType) => state.usersList.data);
    const loading = useSelector((state: RootStoreType) => state.providerDetail.loading);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const shipperCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperCodes);
    const providerSystemCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.providerSystemCodes);
    const clientTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.clientTypeCodes);
    const businessFieldCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.businessFieldCodes);
    const contractTermCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.contractTermCodes);
    const salesRepresentativeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.salesRepresentativeCodes);
    const keyAccountManagerCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.keyAccountManagerCodes);
    const providerStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.providerStatusCodes);
    const providersData = useSelector((state: RootStoreType) => state.providersList.data);
    const applicationInstallData = useSelector((state: RootStoreType) => state.applicationInstall.data);
    const providersCodes: ConstantsCodeType[] = providersData ? providersData.map((provider) => ({
        label: provider.COMPANY_NAME,
        value: String(provider.ID),
        }) as ConstantsCodeType) : [];
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const [editData, setEditData] = useState<ProviderType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [activeTab, setActiveTab] = useState<string>('detail');
    const [selectedServiceCodes, setSelectedServiceCodes] = useState<SelectValueType[]>([]);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('PROVIDER.CREATE');
    const permissionUpdate = useCheckPermission('PROVIDER.UPDATE');
    const permissionOnboardingEdit = useCheckPermission('PROVIDER.ONBOARDING_EDIT');
    const permissionFiles = useCheckPermission('PROVIDER.FILES');

    const disableEdit = newProvider ? !permissionCreate : !permissionUpdate;

    const backUrl = '/admin/providers/';

    useEffect(() => {
            if (!providersData) {
                dispatch(getProvidersListFetch({}));
            }
    }, [providersData]);

    useEffect(() => {
        if (idProvider) {
            dispatch(
                setLocationParams({
                    name: translate('providerDetail.title', { idProvider, eshopName: data?.COMPANY_NAME || '' }),
                    backUrl
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('providerDetail.titleNew'), backUrl }));
        }
    }, [idProvider, backUrl, locationHistory, data]);

    useEffect(() => {
        if (idProvider) {
            dispatch(getProviderDetailFetch(idProvider));
            dispatch(getApplicationInstallFetch(idProvider));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearProviderDetail());
        };
    }, [idProvider]);

    useEffect(() => {
        if (data) {
            // Use getEditData to properly format the data including FILES
            setEditData(getEditData(data, EDIT_KEYS));
            setSelectedServiceCodes(
                data.SERVICE_CODES?.map((serviceCode) => getServiceCodeFromData(serviceCode)) || []
            );
        }

        if (!idProvider && data?.ID) {
            navigate('/admin/providers/' + String(data.ID));
            setThisStorageData(null);
        }
    }, [data]);

    useEffect(() => {
        if (!usersData) {
            dispatch(getUsersListFetch());
        }
    }, [usersData]);

    // save edit data -start
    const editDataRef = useRef<ProviderType>({});
    useEffect(() => {
        if (
            newProvider &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/admin/providers') ||
                samePaths(locationHistory[1]?.pathname, '/admin/providers/new'))
        ) {
            setConfirmParams({
                title: translate('providerDetail.confirmGetNewDataStorage'),
                content: translate('providerDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                }
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: ProviderType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeMultiple = (updates: Partial<ProviderType>) => {
        const newData = { ...editData, ...updates };
        const newErrors = { ...editErrors };
        // Vymazat chyby pro všechny aktualizované klíče
        Object.keys(updates).forEach((key) => {
            delete newErrors[key];
        });
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleChangeTab = (newTab: string, requireValidation?: boolean) => {
        if (requireValidation) {
            const newFormErrors = checkFormValidity(formRef);
            if (!Object.keys(newFormErrors).length) {
                setActiveTab(newTab);
            } else {
                dispatch(
                    addMessage({
                        type: 'warning',
                        title: translate('messages.formWarningTitle'),
                        text: translate('messages.formWarningText')
                    })
                );
            }
            setEditErrors(newFormErrors);
        } else {
            setActiveTab(newTab);
        }
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const formatData = {
                ...(newProvider ? deleteEmptyKeysAndValues(editData) : editData),
                SERVICE_CODES: selectedServiceCodes.map((serviceCode: any): any =>
                    getServiceCodeObject(serviceCode, serviceCodes)
                )
            };

            if (newProvider) {
                dispatch(createProviderDetailFetch({ ...formatData }));
            } else {
                dispatch(updateProviderDetailFetch({ idProvider, data: formatData }));
            }
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock
                data={data}
                idProvider={idProvider}
                newProvider={newProvider}
                disableEdit={disableEdit}
                handleSave={handleSave}
                activeTab={activeTab}
                handleChangeTab={handleChangeTab}
            />
            <form ref={formRef}>
                {loading && <Loading />}
                <RenderComponents
                    data={data}
                    usersData={usersData}
                    handleChangeTab={handleChangeTab}
                    onChange={onChange}
                    onChangeMultiple={onChangeMultiple}
                    editData={editData}
                    editErrors={editErrors}
                    newProvider={newProvider}
                    disableEdit={disableEdit}
                    serviceCodes={serviceCodes || []}
                    shipperCodes={shipperCodes || []}
                    providerSystemCodes={providerSystemCodes}
                    clientTypeCodes={clientTypeCodes || []}
                    businessFieldCodes={businessFieldCodes || []}
                    contractTermCodes={contractTermCodes || []}
                    activeTab={activeTab}
                    selectedServiceCodes={selectedServiceCodes}
                    setSelectedServiceCodes={setSelectedServiceCodes}
                    handleSave={handleSave}
                    permissionOnboardingEdit={permissionOnboardingEdit}
                    permissionFiles={permissionFiles}
                    providersCodes={providersCodes}
                    salesRepresentativeCodes={salesRepresentativeCodes}
                    keyAccountManagerCodes={keyAccountManagerCodes}
                    providerStatusCodes={providerStatusCodes || []}
                    applicationInstallData={applicationInstallData}
                />
            </form>

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ProviderDetailContainer;
